import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { API_BASE_URL } from '@app/core/tokens/api-base-url.token';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { PaginatedResponse } from '@app/core/models/shared/admin.model';
import { PartnerStoreStatus } from '@app/core/models/partner/partner-store-tree.model';
import {
  CreatePartnerStoreRequest,
  PartnerStoreModel,
  SearchPartnerStoresRequest,
  UpdatePartnerStoreRequest,
} from '@app/core/models/partner/partner-store.model';

@Injectable({
  providedIn: 'root',
})
export class PartnerStoreService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  readonly isLoading = signal(false);

  private get baseUrl(): string {
    return `${this.apiBaseUrl}/admin/partner-stores`;
  }

  getStoresByPartnerId(partnerId: string): Observable<PartnerStoreModel[]> {
    if (!partnerId) return of([]);
    this.isLoading.set(true);
    const params = new HttpParams().set('page', '0').set('size', '1000');
    return this.http
      .get<PaginatedResponse<PartnerStoreModel>>(`${this.baseUrl}/by-partner/${partnerId}`, {
        params,
      })
      .pipe(
        map(res => res?.content ?? []),
        catchError(error => {
          console.error('Error loading partner stores:', error);
          this.translate.get('Ошибка при получении магазинов партнера').subscribe(msg => {
            this.snackbarService.error(msg);
          });
          return of([]);
        }),
        finalize(() => this.isLoading.set(false))
      );
  }

  getStoresByNetworkId(networkId: string): Observable<PartnerStoreModel[]> {
    if (!networkId) return of([]);
    this.isLoading.set(true);
    const params = new HttpParams().set('page', '0').set('size', '1000');
    return this.http
      .get<PaginatedResponse<PartnerStoreModel>>(`${this.baseUrl}/by-network/${networkId}`, {
        params,
      })
      .pipe(
        map(res => res?.content ?? []),
        catchError(error => {
          console.error('Error loading partner stores by network:', error);
          this.translate.get('Ошибка при получении магазинов партнера').subscribe(msg => {
            this.snackbarService.error(msg);
          });
          return of([]);
        }),
        finalize(() => this.isLoading.set(false))
      );
  }

  searchPartnerStores(body: SearchPartnerStoresRequest): Observable<PartnerStoreModel[]> {
    const partnerId = body.partnerId ?? null;
    const networkId = body.networkId ?? null;
    const search = (body.search ?? '').trim() || null;
    const status = body.status ?? null;

    this.isLoading.set(true);
    const params = new HttpParams().set('page', '0').set('size', '1000');
    const requestBody: SearchPartnerStoresRequest = {
      partnerId,
      networkId,
      search,
      status,
    };

    return this.http
      .post<PaginatedResponse<PartnerStoreModel>>(`${this.baseUrl}/search`, requestBody, { params })
      .pipe(
        map(res => res?.content ?? []),
        catchError(error => {
          console.error('Error loading partner stores:', error);
          this.translate.get('Ошибка при получении магазинов партнера').subscribe(msg => {
            this.snackbarService.error(msg);
          });
          return of([]);
        }),
        finalize(() => this.isLoading.set(false))
      );
  }

  getStoreById(storeId: string): Observable<PartnerStoreModel | null> {
    if (!storeId) return of(null);
    this.isLoading.set(true);
    return this.http.get<PartnerStoreModel>(`${this.baseUrl}/${storeId}`).pipe(
      catchError(error => {
        console.error('Error loading store:', error);
        this.translate.get('Ошибка при получении магазина партнера').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of(null);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  createStore(body: CreatePartnerStoreRequest): Observable<PartnerStoreModel> {
    this.isLoading.set(true);
    return this.http.post<PartnerStoreModel>(this.baseUrl, body).pipe(
      catchError(error => {
        console.error('Error creating store:', error);
        this.translate.get('Ошибка при создании магазина партнера').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  updateStore(storeId: string, body: UpdatePartnerStoreRequest): Observable<PartnerStoreModel> {
    if (!storeId) throw new Error('storeId is required');
    this.isLoading.set(true);
    return this.http.put<PartnerStoreModel>(`${this.baseUrl}/${storeId}`, body).pipe(
      catchError(error => {
        console.error('Error updating store:', error);
        this.translate.get('Ошибка при обновлении магазина партнера').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  deleteStore(storeId: string): Observable<void> {
    if (!storeId) throw new Error('storeId is required');
    this.isLoading.set(true);
    return this.http.delete<void>(`${this.baseUrl}/${storeId}`).pipe(
      catchError(error => {
        console.error('Error deleting store:', error);
        this.translate.get('Ошибка при удалении магазина партнера').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  /** Отдельного эндпоинта статуса нет — PUT со всеми текущими полями магазина и новым статусом */
  setStoreStatus(store: PartnerStoreModel, status: PartnerStoreStatus): Observable<PartnerStoreModel> {
    const body: UpdatePartnerStoreRequest = {
      name: store.name,
      city: store.city,
      street: store.street,
      address: store.address,
      phone: store.phone,
      email: store.email,
      latitude: store.latitude,
      longitude: store.longitude,
      status,
    };
    return this.updateStore(store.id, body);
  }
}
